import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import { usePersona } from "../persona-context";

type ReviewStatus = "todo" | "progress" | "done";
type ReviewDecision = "Geverifieerd" | "Niet relevant";
type ReviewStepId = "briefing" | "nalopen" | "issues" | "advies";
type ReviewTab = "mine" | "agenda";

interface ReviewIssue {
  id: string;
  title: string;
  severity: "hoog" | "midden" | "laag";
  source: string;
  tip: string;
  /** Reference to the dossier question this maps to, e.g. "Luik C · Vraag 1": same convention as the intake wizard's IntakeField.luik. */
  luik: string;
  aiFinding: string;
  reviewerNote: string;
}

type QuestionVerdict = "geen-opmerking" | "aandachtspunt";

interface ReviewQuestion {
  /** Stable id, e.g. "opzet". Questions that map to a seeded issue reuse that issue's id. */
  id: string;
  /** "Luik C": used for rail grouping headers. */
  luikGroup: string;
  /** "Luik C · Vraag 9": same convention as the intake wizard's IntakeField.luik. */
  luik: string;
  /** Applicant-facing question label, taken from the intake wizard. */
  question: string;
  /** The researcher's answer, shown read-only. */
  answer: string;
  /** AI pre-flagged this question → default verdict "aandachtspunt". */
  aiFlagged?: boolean;
  /** Shown in the blue AI-signaal callout when flagged. */
  aiNote?: string;
  /** Pre-fills the aandachtspunt feedback textarea. */
  aiSuggestion?: string;
}

interface AssignedReview {
  ref: string;
  researcher: string;
  project: string;
  faculty: string;
  type: string;
  status: ReviewStatus;
  due: string;
  minutes: string;
  summary: string;
  aiConfidence: string;
  dossierPieces: string[];
  issues: ReviewIssue[];
  questions: ReviewQuestion[];
  draftAdvice: string;
  outOfScope?: boolean;
}

const REVIEW_STEPS: Array<{ id: ReviewStepId; label: string; eyebrow: string; title: string }> = [
  {
    id: "briefing",
    label: "Briefing",
    eyebrow: "Stap 1 · Begrijp het dossier",
    title: "Lees de AI-briefing en controleer de bronnen",
  },
  {
    id: "nalopen",
    label: "Nalopen",
    eyebrow: "Stap 2 · Loop de aanvraag na",
    title: "Beoordeel elke vraag van de aanvraag",
  },
  {
    id: "issues",
    label: "Bevindingen",
    eyebrow: "Stap 3 · Valideer AI-bevindingen",
    title: "Beslis per aandachtspunt wat overeind blijft",
  },
  {
    id: "advies",
    label: "Feedback",
    eyebrow: "Stap 4 · Menselijk oordeel",
    title: "Maak de feedback adviesklaar",
  },
];

const ASSIGNED_REVIEWS: AssignedReview[] = [
  {
    ref: "EA-2026-032",
    researcher: "Hanne Smeets",
    project: "Interviews over dakloosheid en verslaving",
    faculty: "GGW",
    type: "Interviewstudie",
    status: "todo",
    due: "Voor woensdag 2 jul",
    minutes: "8 min",
    aiConfidence: "86%",
    summary:
      "Kwalitatieve interviewstudie bij ongeveer 15 tot 20 volwassenen die het afgelopen jaar dakloosheid en/of een verslavingsproblematiek hebben ervaren. Werving verloopt via drie partnerorganisaties (nachtopvang, laagdrempelige verslavingszorg en een buddywerking); de onderzoeker komt pas met een deelnemer in contact nadat een hulpverlener die persoon heeft voorgesteld. De semigestructureerde interviews duren 45 tot 60 minuten, worden met toestemming audio-opgenomen en na transcriptie gepseudonimiseerd, waarna de opname binnen twee weken wordt vernietigd. Het protocol beschrijft een stopcriterium bij zichtbare emotionele belasting, maar het dossier maakt nog niet concreet wie dat signaal tijdens het gesprek opvolgt en naar welke hulpverlening wordt doorverwezen. De informed consent-tekst bevat alle verplichte onderdelen, maar het taalniveau ligt hoger dan gangbaar is bij deze doelgroep.",
    dossierPieces: [
      "Protocol v2 · secties deelnemers, rekrutering, risico's",
      "Informed consent NL · versie 24 juni",
      "Datamanagementplan · pseudonimisering en bewaartermijn",
      "Opnameprotocol · audio-opname, transcriptie en bewaartermijn",
    ],
    issues: [
      {
        id: "vulnerability",
        title: "Kwetsbare doelgroep vraagt extra duiding",
        severity: "hoog",
        source: "Protocol · p. 4-5",
        tip: "Tip sheet 9/10 · kwetsbaarheid",
        luik: "Luik C · Vraag 9–10",
        aiFinding:
          "De studie benoemt dakloosheid en verslaving, maar maakt nog niet concreet wie signalen van distress opvolgt en wanneer een interview wordt stopgezet.",
        reviewerNote: "Vraag om kort stopcriterium en doorverwijsroute toe te voegen.",
      },
      {
        id: "consent",
        title: "Consenttekst is correct maar erg juridisch",
        severity: "midden",
        source: "Informed consent · p. 2",
        tip: "Tip sheet 2/3 · geïnformeerde toestemming",
        luik: "Luik C · Vraag 2–3",
        aiFinding:
          "De toestemmingsbrief bevat alle verplichte elementen, maar het taalniveau lijkt hoog voor de beoogde populatie.",
        reviewerNote:
          "Vraag een plain-language alinea over vrijwilligheid en stoppen zonder gevolgen.",
      },
    ],
    questions: [
      {
        id: "titel",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Titel van het ethisch advies",
        answer: "Interviews over ervaringen met dakloosheid en verslaving",
      },
      {
        id: "type",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Soort onderzoek",
        answer: "Semigestructureerde interviews · kwalitatief",
      },
      {
        id: "looptijd",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Verwachte looptijd",
        answer: "Start na goedkeuring · einde 31/12/2026",
      },
      {
        id: "databeheer",
        luikGroup: "Luik B",
        luik: "Luik B · 1",
        question: "Databeheer conform GDPR/AVG bevestigd?",
        answer:
          "Ja. Transcripties worden gepseudonimiseerd; de audio-opname wordt binnen twee weken vernietigd.",
      },
      {
        id: "opzet",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 1",
        question: "(A) Studieopzet",
        answer:
          "Kwalitatieve interviewstudie bij 15 tot 20 volwassenen die het afgelopen jaar dakloosheid en/of verslaving hebben ervaren. Interviews duren 45 tot 60 minuten.",
      },
      {
        id: "consent",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 2–3",
        question: "Wijk je af van actieve schriftelijke geïnformeerde toestemming?",
        answer:
          "Nee. Toestemming wordt vooraf schriftelijk bekomen via een consentformulier (zie bijlage informed consent NL).",
        aiFlagged: true,
        aiNote:
          "De toestemmingsbrief bevat alle verplichte elementen, maar het taalniveau lijkt hoog voor de beoogde populatie.",
        aiSuggestion:
          "Vraag een plain-language alinea over vrijwilligheid en stoppen zonder gevolgen.",
      },
      {
        id: "herkenbaarheid",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 4",
        question: "Kunnen personen geïdentificeerd worden in analyse-data of resultaten?",
        answer:
          "Nee. Namen en contactgegevens worden na transcriptie verwijderd; citaten worden gepseudonimiseerd gerapporteerd.",
      },
      {
        id: "vergoeding",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 5–6",
        question: "Bied je vergoedingen of extra's?",
        answer: "Nee, er is geen vergoeding voor deelnemers.",
      },
      {
        id: "minderjarigen",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 7–8",
        question: "Reken je deelnemers jonger dan 18 jaar?",
        answer: "Nee, alle deelnemers zijn 18 jaar of ouder.",
      },
      {
        id: "vulnerability",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 9–10",
        question: "Werk je met deelnemers die vooraf erg kwetsbaar zijn?",
        answer:
          "Ja. Deelnemers hebben dakloosheid en/of een verslavingsproblematiek ervaren. Er is een stopcriterium bij zichtbare emotionele belasting.",
        aiFlagged: true,
        aiNote:
          "De studie benoemt dakloosheid en verslaving, maar maakt nog niet concreet wie signalen van distress opvolgt en wanneer een interview wordt stopgezet.",
        aiSuggestion: "Vraag om kort stopcriterium en doorverwijsroute toe te voegen.",
      },
      {
        id: "opnames",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 12",
        question: "Maak of bewaar je audio- of video-opnames?",
        answer:
          "Ja. Interviews worden met toestemming audio-opgenomen, na transcriptie gepseudonimiseerd en de opname wordt binnen twee weken vernietigd.",
      },
      {
        id: "machtsonevenwicht",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 16",
        question: "Is er een mogelijke context van ongelijkheid of machtsonevenwicht?",
        answer:
          "Beperkt. Werving verloopt via hulpverleners van partnerorganisaties; deelname staat expliciet los van de hulpverlening die de deelnemer ontvangt.",
      },
    ],
    draftAdvice:
      "Voorwaardelijk gunstig, mits de onderzoeker de distressprocedure verduidelijkt en de consenttekst toegankelijker formuleert.",
  },
  {
    ref: "EA-2026-030",
    researcher: "Wouter Bogaerts",
    project: "Experiment over besluitvorming onder stress",
    faculty: "LW",
    type: "Experiment",
    status: "progress",
    due: "Vandaag",
    minutes: "12 min",
    aiConfidence: "91%",
    summary:
      "Labexperiment waarin deelnemers tijdsdruk en stressprikkels ervaren tijdens beslissingsopdrachten. De AI ziet vooral vragen rond debriefing, stopcriteria en proportionaliteit van stressinductie.",
    dossierPieces: [
      "Protocol v3 · stressinductie en taken",
      "Debriefingtekst · concept",
      "Rekruteringsmail studentenpool",
    ],
    issues: [
      {
        id: "debrief",
        title: "Debriefing na stressinductie is te summier",
        severity: "hoog",
        source: "Debriefingtekst · p. 1",
        tip: "Tip sheet 13 · deception en debriefing",
        luik: "Luik C · Vraag 12–13",
        aiFinding:
          "De debriefing legt het doel uit, maar beschrijft niet hoe deelnemers vragen kunnen stellen of ondersteuning krijgen.",
        reviewerNote: "Aanvullen met contactpunt en expliciete check-out na afloop.",
      },
      {
        id: "burden",
        title: "Belasting lijkt proportioneel, maar onderbouw de duur",
        severity: "midden",
        source: "Protocol · p. 7",
        tip: "Tip sheet 1 · studiepopulatie",
        luik: "Luik C · Vraag 5–6",
        aiFinding:
          "De stressprikkel duurt maximaal twaalf minuten; de onderbouwing waarom dit nodig en proportioneel is ontbreekt nog.",
        reviewerNote: "Vraag korte rationale en verwijzing naar eerdere studies.",
      },
    ],
    questions: [
      {
        id: "titel",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Titel van het ethisch advies",
        answer: "Experiment over besluitvorming onder tijdsdruk en stress",
      },
      {
        id: "type",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Soort onderzoek",
        answer: "Labexperiment",
      },
      {
        id: "databeheer",
        luikGroup: "Luik B",
        luik: "Luik B · 1",
        question: "Databeheer conform GDPR/AVG bevestigd?",
        answer:
          "Ja. Meetgegevens worden gepseudonimiseerd verwerkt en apart van contactgegevens bewaard.",
      },
      {
        id: "opzet",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 1",
        question: "(A) Studieopzet",
        answer:
          "Labexperiment waarin deelnemers beslissingsopdrachten uitvoeren onder tijdsdruk en stressprikkels. Deelnemers komen uit de studentenpool.",
      },
      {
        id: "consent",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 2",
        question: "Wijk je af van actieve schriftelijke geïnformeerde toestemming?",
        answer: "Nee. Deelnemers tekenen vooraf een geïnformeerd toestemmingsformulier.",
      },
      {
        id: "burden",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 5–6",
        question: "Is de belasting voor deelnemers proportioneel onderbouwd?",
        answer: "De stressprikkel duurt maximaal twaalf minuten per deelnemer.",
        aiFlagged: true,
        aiNote:
          "De stressprikkel duurt maximaal twaalf minuten; de onderbouwing waarom dit nodig en proportioneel is ontbreekt nog.",
        aiSuggestion: "Vraag korte rationale en verwijzing naar eerdere studies.",
      },
      {
        id: "minderjarigen",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 7–8",
        question: "Reken je deelnemers jonger dan 18 jaar?",
        answer: "Nee, deelnemers uit de studentenpool zijn 18 jaar of ouder.",
      },
      {
        id: "debrief",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 12–13",
        question: "Beschrijf je debriefing-procedure",
        answer:
          "Deelnemers krijgen na afloop een korte uitleg over het doel van het experiment en de gebruikte stressprikkels.",
        aiFlagged: true,
        aiNote:
          "De debriefing legt het doel uit, maar beschrijft niet hoe deelnemers vragen kunnen stellen of ondersteuning krijgen.",
        aiSuggestion: "Aanvullen met contactpunt en expliciete check-out na afloop.",
      },
    ],
    draftAdvice:
      "Nog niet adviesklaar: de debriefing en proportionaliteit van de stressprocedure moeten aangescherpt worden.",
    outOfScope: true,
  },
  {
    ref: "EA-2026-028",
    researcher: "Layla Benali",
    project: "Survey over discriminatie op de arbeidsmarkt",
    faculty: "TEW",
    type: "Survey",
    status: "done",
    due: "Gereviewd",
    minutes: "5 min",
    aiConfidence: "88%",
    summary:
      "Online survey bij volwassen respondenten over ervaren discriminatie in sollicitatieprocedures. Geen hoog-risico bevindingen na menselijke validatie.",
    dossierPieces: [
      "Surveyvragenlijst · definitief",
      "Informed consent online",
      "Datamanagementplan · anonieme verwerking",
    ],
    issues: [
      {
        id: "anonymous",
        title: "Anonieme verwerking voldoende onderbouwd",
        severity: "laag",
        source: "Datamanagementplan · p. 2",
        tip: "Flowchart · is mijn studie anoniem?",
        luik: "Luik C · Vraag 14–15",
        aiFinding:
          "Er worden geen direct identificerende gegevens verzameld en IP-adressen worden niet opgeslagen.",
        reviewerNote: "Bevestigd; geen extra feedback nodig.",
      },
    ],
    questions: [
      {
        id: "titel",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Titel van het ethisch advies",
        answer: "Survey over ervaren discriminatie op de arbeidsmarkt",
      },
      {
        id: "type",
        luikGroup: "Luik A",
        luik: "Luik A",
        question: "Soort onderzoek",
        answer: "Anonieme online vragenlijst",
      },
      {
        id: "databeheer",
        luikGroup: "Luik B",
        luik: "Luik B · 1",
        question: "Databeheer conform GDPR/AVG bevestigd?",
        answer: "Ja. Er worden geen persoonsgegevens verzameld die tot identificatie leiden.",
      },
      {
        id: "opzet",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 1",
        question: "(A) Studieopzet",
        answer:
          "Anonieme online survey bij volwassen respondenten over ervaren discriminatie in sollicitatieprocedures.",
      },
      {
        id: "herkenbaarheid",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 4",
        question: "Kunnen personen geïdentificeerd worden in analyse-data of resultaten?",
        answer: "Nee. Er worden geen direct identificerende gegevens verzameld.",
      },
      {
        id: "anonymous",
        luikGroup: "Luik C",
        luik: "Luik C · Vraag 14–15",
        question: "Zal je niet-anonieme data later voor andere doeleinden gebruiken?",
        answer:
          "Nee. Enkel anonieme data worden bewaard; IP-adressen worden door de survey-tool niet opgeslagen.",
        aiFlagged: true,
        aiNote:
          "Er worden geen direct identificerende gegevens verzameld en IP-adressen worden niet opgeslagen.",
        aiSuggestion: "Bevestigd; geen extra feedback nodig.",
      },
    ],
    draftAdvice: "Gunstig advies. Geen bijkomende voorwaarden vanuit de reviewer.",
    outOfScope: true,
  },
];

const AGENDA_ROWS = [
  [
    "EA-2026-040",
    "Tom Vermeulen",
    "Observatiestudie op de arbeidsmarkt",
    "Prof. Claessens",
    "Bezig",
  ],
  ["EA-2026-038", "Jonas Willems", "Longitudinale studie in de ouderenzorg", "Dr. Maes", "Bezig"],
  [
    "EA-2026-034",
    "Bert Janssen",
    "Casestudy over digitale geletterdheid",
    "Prof. Verstraeten",
    "Gereviewd",
  ],
  [
    "EA-2026-032",
    "Hanne Smeets",
    "Interviews over dakloosheid en verslaving",
    "Dr. Peeters",
    "Nog te reviewen",
  ],
  [
    "EA-2026-030",
    "Wouter Bogaerts",
    "Experiment over besluitvorming onder stress",
    "Dr. Peeters",
    "Bezig",
  ],
  [
    "EA-2026-028",
    "Layla Benali",
    "Survey over discriminatie op de arbeidsmarkt",
    "Dr. Peeters",
    "Gereviewd",
  ],
] as const;

/**
 * Seeds the per-question triage state from a review: AI-flagged questions start
 * as "aandachtspunt" with the AI suggestion pre-filled, so the Nalopen step and
 * the Bevindingen step agree from the first render.
 */
function buildQuestionState(review: AssignedReview) {
  const verdicts: Record<string, QuestionVerdict> = {};
  const notes: Record<string, string> = {};
  for (const question of review.questions) {
    if (question.aiFlagged) {
      verdicts[question.id] = "aandachtspunt";
      notes[question.id] = question.aiSuggestion ?? "";
    }
  }
  return { verdicts, notes };
}

export function BureauPage() {
  const { persona } = usePersona();
  const [searchParams] = useSearchParams();
  const [tab] = useState<ReviewTab>(searchParams.get("tab") === "agenda" ? "agenda" : "mine");
  const isSecretariaatAgendaView = persona === "backoffice" && tab === "agenda";
  const [selectedRef, setSelectedRef] = useState(ASSIGNED_REVIEWS[0]?.ref ?? "");
  const [currentStep, setCurrentStep] = useState(0);
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({});
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [questionVerdicts, setQuestionVerdicts] = useState<Record<string, QuestionVerdict>>(() => {
    const first = ASSIGNED_REVIEWS[0];
    return first ? buildQuestionState(first).verdicts : {};
  });
  const [questionNotes, setQuestionNotes] = useState<Record<string, string>>(() => {
    const first = ASSIGNED_REVIEWS[0];
    return first ? buildQuestionState(first).notes : {};
  });

  const selected = useMemo(
    () => ASSIGNED_REVIEWS.find((review) => review.ref === selectedRef) ?? ASSIGNED_REVIEWS[0],
    [selectedRef],
  );

  // The Bevindingen step shows the live union of every question currently
  // marked "aandachtspunt" (both AI-confirmed and reviewer-added) instead of
  // the static issues array. Metadata (severity, source, tip) is inherited from
  // the seeded issue when the question maps to one, else sensible defaults.
  const activeIssues = useMemo<ReviewIssue[]>(() => {
    if (!selected) {
      return [];
    }
    const issueById = new Map(selected.issues.map((issue) => [issue.id, issue]));
    return selected.questions
      .filter((question) => questionVerdicts[question.id] === "aandachtspunt")
      .map((question) => {
        const mapped = issueById.get(question.id);
        return {
          id: question.id,
          title: question.question,
          severity: mapped?.severity ?? "midden",
          source: mapped?.source ?? question.luik,
          tip: mapped?.tip ?? "",
          luik: question.luik,
          aiFinding: question.aiNote ?? "Aandachtspunt toegevoegd door reviewer",
          reviewerNote: questionNotes[question.id] ?? question.aiSuggestion ?? "",
        };
      });
  }, [selected, questionVerdicts, questionNotes]);

  if (!selected) {
    return null;
  }

  const step = REVIEW_STEPS[currentStep] ?? REVIEW_STEPS[0]!;
  const reviewedCount = ASSIGNED_REVIEWS.filter((review) => review.status === "done").length;
  const openCount = ASSIGNED_REVIEWS.filter((review) => review.status !== "done").length;
  const decisionCount = activeIssues.filter((issue) => decisions[issue.id]).length;

  function selectReview(ref: string) {
    setSelectedRef(ref);
    setCurrentStep(0);
    setDecisions({});
    setNoteDrafts({});
    const review = ASSIGNED_REVIEWS.find((item) => item.ref === ref);
    if (review) {
      const { verdicts, notes } = buildQuestionState(review);
      setQuestionVerdicts(verdicts);
      setQuestionNotes(notes);
    }
  }

  function setQuestionVerdict(id: string, verdict: QuestionVerdict) {
    setQuestionVerdicts((prev) => ({ ...prev, [id]: verdict }));
    // Leaving the "aandachtspunt" list drops this question out of Bevindingen.
    // Any stale verify/note tied to its old aandachtspunt life must go with it,
    // otherwise re-flagging it later resurrects a decision no one actually made.
    if (verdict !== "aandachtspunt") {
      clearDecision(id);
      setNoteDrafts((prev) => {
        if (!(id in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  function setQuestionNote(id: string, text: string) {
    setQuestionNotes((prev) => ({ ...prev, [id]: text }));
  }

  function setDecision(issueId: string, decision: ReviewDecision | undefined) {
    if (!decision) {
      clearDecision(issueId);
      return;
    }
    setDecisions((prev) => ({ ...prev, [issueId]: decision }));
  }

  function clearDecision(issueId: string) {
    setDecisions((prev) => {
      if (!(issueId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[issueId];
      return next;
    });
  }

  function updateNote(issueId: string, text: string) {
    setNoteDrafts((prev) => ({ ...prev, [issueId]: text }));
    // Editing a verified AI-suggestion invalidates the verification. The
    // reviewer must re-verify the edited text before it can be used.
    if (decisions[issueId] === "Geverifieerd") {
      clearDecision(issueId);
    }
  }

  if (isSecretariaatAgendaView) {
    return (
      <section>
        <div className="page-head">
          <div>
            <p className="eyebrow">Secretariaat · Bureauoverzicht</p>
            <h1>Bureau-agenda</h1>
            <p
              className="ua-muted"
              style={{ marginTop: "8px", maxWidth: "720px", fontSize: "16px" }}
            >
              Overzicht van de dossiers die op de eerstvolgende bureauvergadering staan.
            </p>
          </div>
        </div>

        <BureauAgenda />
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <p className="eyebrow">Reviewerwerkplek · AI-ondersteunde review</p>
          <h1>Mijn toegewezen reviews</h1>
          <p className="ua-muted" style={{ marginTop: "8px", maxWidth: "720px", fontSize: "16px" }}>
            Werk je eigen dossiers af in een begeleide flow. AI vat samen, wijst op bronnen en
            bereidt tekst voor; jij valideert en formuleert het menselijke oordeel.
          </p>
        </div>
      </div>

      <div className="stats ua-mb-space">
        <div className="stat">
          <div className="stat__label">Toegewezen aan mij</div>
          <div className="stat__value">{ASSIGNED_REVIEWS.length}</div>
          <div className="stat__delta">voor bureau 2 jul</div>
        </div>
        <div className="stat">
          <div className="stat__label">Nog te reviewen</div>
          <div className="stat__value">{openCount}</div>
          <div className="stat__delta">incl. work in progress</div>
        </div>
        <div className="stat">
          <div className="stat__label">Gereviewd</div>
          <div className="stat__value">{reviewedCount}</div>
          <div className="stat__delta up">klaar voor bureau</div>
        </div>
      </div>

      <div className="reviewer-layout">
        <ReviewQueue selectedRef={selected.ref} onSelect={selectReview} />
        <ReviewWorkspace
          review={selected}
          step={step}
          currentStep={currentStep}
          activeIssues={activeIssues}
          decisions={decisions}
          decisionCount={decisionCount}
          noteDrafts={noteDrafts}
          questionVerdicts={questionVerdicts}
          questionNotes={questionNotes}
          onStep={setCurrentStep}
          onDecision={setDecision}
          onNoteChange={updateNote}
          onQuestionVerdict={setQuestionVerdict}
          onQuestionNote={setQuestionNote}
        />
      </div>
    </section>
  );
}

function ReviewQueue({
  selectedRef,
  onSelect,
}: {
  selectedRef: string;
  onSelect: (ref: string) => void;
}) {
  return (
    <aside className="card" style={{ overflow: "hidden" }}>
      <div className="card__head">
        <div>
          <h2>Overzicht</h2>
          <p className="ua-muted-sm">Alleen dossiers die aan jou zijn toegewezen</p>
        </div>
      </div>
      <div style={{ display: "grid" }}>
        {ASSIGNED_REVIEWS.map((review) => (
          <button
            key={review.ref}
            onClick={() =>
              review.outOfScope
                ? toast("Out of Scope", {
                    description: `${review.researcher} is buiten scope voor deze demo.`,
                  })
                : onSelect(review.ref)
            }
            style={{
              appearance: "none",
              border: "none",
              borderTop: "1px solid var(--ua-border)",
              borderLeft:
                selectedRef === review.ref ? "4px solid var(--ua-red)" : "4px solid transparent",
              background: selectedRef === review.ref ? "#f8fafc" : "white",
              textAlign: "left",
              padding: "16px",
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
              <span className="ua-bold-sm">{review.researcher}</span>
              <StatusBadge status={review.status} />
            </div>
            <div className="ua-muted-xs" style={{ marginTop: "4px" }}>
              {review.ref} · {review.faculty} · {review.type}
            </div>
            <div style={{ color: "var(--ua-muted)", fontSize: "12px", marginTop: "8px" }}>
              {review.project}
            </div>
            <div className="ua-muted-xs" style={{ marginTop: "10px" }}>
              {review.due} · geschat {review.minutes}
            </div>
          </button>
        ))}
      </div>
    </aside>
  );
}

function ReviewWorkspace({
  review,
  step,
  currentStep,
  activeIssues,
  decisions,
  decisionCount,
  noteDrafts,
  questionVerdicts,
  questionNotes,
  onStep,
  onDecision,
  onNoteChange,
  onQuestionVerdict,
  onQuestionNote,
}: {
  review: AssignedReview;
  step: (typeof REVIEW_STEPS)[number];
  currentStep: number;
  activeIssues: ReviewIssue[];
  decisions: Record<string, ReviewDecision>;
  decisionCount: number;
  noteDrafts: Record<string, string>;
  questionVerdicts: Record<string, QuestionVerdict>;
  questionNotes: Record<string, string>;
  onStep: (index: number) => void;
  onDecision: (issueId: string, decision: ReviewDecision | undefined) => void;
  onNoteChange: (issueId: string, text: string) => void;
  onQuestionVerdict: (id: string, verdict: QuestionVerdict) => void;
  onQuestionNote: (id: string, text: string) => void;
}) {
  const isLast = currentStep === REVIEW_STEPS.length - 1;
  const onIssuesStep = step.id === "issues";
  const issuesRemaining = activeIssues.length - decisionCount;
  const blockedByIssues = onIssuesStep && issuesRemaining > 0;

  const onNalopenStep = step.id === "nalopen";
  const questionsReviewed = review.questions.filter(
    (question) => questionVerdicts[question.id],
  ).length;
  const questionsRemaining = review.questions.length - questionsReviewed;
  const blockedByQuestions = onNalopenStep && questionsRemaining > 0;

  return (
    <article className="card reviewer-workspace" style={{ overflow: "hidden" }}>
      <div className="card__head" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow" style={{ marginBottom: "4px" }}>
            {review.ref} · {review.faculty}
          </p>
          <h2 style={{ fontSize: "20px" }}>{review.project}</h2>
          <p className="ua-muted-sm" style={{ marginTop: "6px" }}>
            {review.researcher} · {review.type}
          </p>
        </div>
        <StatusBadge status={review.status} />
      </div>

      <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--ua-border)" }}>
        <div className="stepper">
          {REVIEW_STEPS.map((reviewStep, index) => {
            const state =
              index < currentStep ? "step--done" : index === currentStep ? "step--active" : "";
            return (
              <button
                key={reviewStep.id}
                className={`step ${state}`}
                onClick={() => onStep(index)}
                style={{ background: "none", border: "none", cursor: "pointer", font: "inherit" }}
              >
                <span className="step__dot">{index < currentStep ? "✓" : index + 1}</span>
                <span className="step__label">{reviewStep.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: "22px 24px" }}>
        <p className="eyebrow" style={{ marginBottom: "4px" }}>
          {step.eyebrow}
        </p>
        <h2 style={{ fontSize: "20px", marginBottom: "16px" }}>{step.title}</h2>

        {step.id === "briefing" && <BriefingStep review={review} />}
        {step.id === "nalopen" && (
          <QuestionReviewStep
            review={review}
            questionVerdicts={questionVerdicts}
            questionNotes={questionNotes}
            onQuestionVerdict={onQuestionVerdict}
            onQuestionNote={onQuestionNote}
          />
        )}
        {step.id === "issues" && (
          <IssuesStep
            issues={activeIssues}
            decisions={decisions}
            noteDrafts={noteDrafts}
            onDecision={onDecision}
            onNoteChange={onNoteChange}
          />
        )}
        {step.id === "advies" && (
          <AdviceStep
            review={review}
            issueCount={activeIssues.length}
            decisionCount={decisionCount}
          />
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          padding: "16px 20px",
          borderTop: "1px solid var(--ua-border)",
          background: "var(--ua-bg)",
        }}
      >
        <button
          className="btn btn--ghost"
          disabled={currentStep === 0}
          onClick={() => onStep(Math.max(0, currentStep - 1))}
        >
          ← Vorige
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {onNalopenStep && questionsRemaining > 0 && (
            <VerifyProgress
              verified={questionsReviewed}
              total={review.questions.length}
              label="Beoordeel eerst elke vraag op deze stap."
              countLabel="nagekeken"
            />
          )}
          {onIssuesStep && issuesRemaining > 0 && (
            <VerifyProgress verified={decisionCount} total={activeIssues.length} />
          )}
          <button
            className="btn btn--primary"
            onClick={() => onStep(Math.min(REVIEW_STEPS.length - 1, currentStep + 1))}
            disabled={isLast || blockedByIssues || blockedByQuestions}
          >
            Volgende →
          </button>
        </div>
      </div>
    </article>
  );
}

function VerifyProgress({
  verified,
  total,
  label = "Verifieer eerst alle AI-voorstellen op deze stap.",
  countLabel,
}: {
  verified: number;
  total: number;
  label?: string;
  countLabel?: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ fontSize: "12px", color: "var(--ua-muted)" }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            title={i < verified ? "Afgehandeld" : "Nog te verifiëren"}
            style={{
              width: "9px",
              height: "9px",
              borderRadius: "50%",
              background: i < verified ? "var(--ua-success)" : "var(--ua-border)",
              transition: "background 0.2s ease, transform 0.2s ease",
              transform: i === verified - 1 ? "scale(1.15)" : "scale(1)",
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--ua-navy)" }}>
        {verified}/{total}
        {countLabel ? ` ${countLabel}` : ""}
      </span>
    </div>
  );
}

function BriefingStep({ review }: { review: AssignedReview }) {
  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <div
        style={{
          border: "1px solid var(--ua-border)",
          borderRadius: "8px",
          padding: "16px",
          background: "#f8fafc",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
          <span className="section-kicker">AI-briefing</span>
        </div>
        <p
          style={{ color: "var(--ua-ink)", fontSize: "14px", lineHeight: 1.65, margin: "12px 0 0" }}
        >
          {review.summary}
        </p>
      </div>

      <div>
        <p className="section-kicker" style={{ marginBottom: "10px" }}>
          Gebruikte dossierstukken
        </p>
        <div style={{ display: "grid", gap: "10px" }}>
          {review.dossierPieces.map((piece) => (
            <div
              key={piece}
              style={{
                border: "1px solid var(--ua-border)",
                borderRadius: "8px",
                padding: "12px 14px",
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <span className="ua-bold-sm">{piece}</span>
              <span className="badge">Open</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function questionDotStyle(verdict: QuestionVerdict | undefined, aiFlagged: boolean) {
  if (verdict === "aandachtspunt") {
    return { background: "var(--ua-red)", glyph: "!", color: "#fff" };
  }
  if (verdict === "geen-opmerking") {
    return { background: "var(--ua-success)", glyph: "✓", color: "#fff" };
  }
  return {
    background: aiFlagged ? "#fbe1e1" : "var(--ua-border)",
    glyph: "",
    color: "var(--ua-muted)",
  };
}

function QuestionReviewStep({
  review,
  questionVerdicts,
  questionNotes,
  onQuestionVerdict,
  onQuestionNote,
}: {
  review: AssignedReview;
  questionVerdicts: Record<string, QuestionVerdict>;
  questionNotes: Record<string, string>;
  onQuestionVerdict: (id: string, verdict: QuestionVerdict) => void;
  onQuestionNote: (id: string, text: string) => void;
}) {
  const questions = review.questions;
  const [activeIndex, setActiveIndex] = useState(0);
  const total = questions.length;
  const safeIndex = Math.min(activeIndex, Math.max(0, total - 1));
  const active = questions[safeIndex];
  const reviewedCount = questions.filter((question) => questionVerdicts[question.id]).length;

  // Group questions by luikGroup in first-appearance order for the left rail.
  const groups: Array<{ luikGroup: string; items: ReviewQuestion[] }> = [];
  for (const question of questions) {
    let group = groups.find((entry) => entry.luikGroup === question.luikGroup);
    if (!group) {
      group = { luikGroup: question.luikGroup, items: [] };
      groups.push(group);
    }
    group.items.push(question);
  }

  function findNextPending(fromIndex: number, justSetId: string) {
    for (let step = 1; step <= total; step += 1) {
      const index = (fromIndex + step) % total;
      const question = questions[index];
      if (!question || question.id === justSetId) {
        continue;
      }
      if (!questionVerdicts[question.id]) {
        return index;
      }
    }
    return -1;
  }

  function handleVerdict(question: ReviewQuestion, verdict: QuestionVerdict) {
    onQuestionVerdict(question.id, verdict);
    // Geen opmerking is terminal; jump to the next open question so the
    // reviewer keeps momentum. Aandachtspunt keeps focus for the note.
    if (verdict !== "aandachtspunt") {
      const next = findNextPending(safeIndex, question.id);
      if (next !== -1) {
        setActiveIndex(next);
      }
    }
  }

  if (!active) {
    return null;
  }

  const activeVerdict = questionVerdicts[active.id];
  const noteValue = questionNotes[active.id] ?? active.aiSuggestion ?? "";
  const isUnEditedAiSuggestion = Boolean(
    active.aiFlagged && active.aiSuggestion && noteValue === active.aiSuggestion,
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(220px, 280px) 1fr",
        gap: "20px",
        alignItems: "start",
      }}
    >
      <aside
        style={{
          border: "1px solid var(--ua-border)",
          borderRadius: "8px",
          overflow: "hidden",
          background: "var(--ua-white)",
        }}
      >
        <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--ua-border)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <span className="section-kicker">Vragen</span>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--ua-navy)" }}>
              {reviewedCount}/{total} nagekeken
            </span>
          </div>
          <div
            style={{
              marginTop: "8px",
              height: "6px",
              borderRadius: "999px",
              background: "var(--ua-border)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${total === 0 ? 0 : (reviewedCount / total) * 100}%`,
                height: "100%",
                background: "var(--ua-success)",
                transition: "width 0.2s ease",
              }}
            />
          </div>
        </div>
        <div style={{ display: "grid" }}>
          {groups.map((group) => (
            <div key={group.luikGroup}>
              <div
                style={{
                  padding: "8px 14px",
                  background: "var(--ua-bg)",
                  borderTop: "1px solid var(--ua-border)",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "var(--ua-muted)",
                }}
              >
                {group.luikGroup}
              </div>
              {group.items.map((question) => {
                const index = questions.findIndex((entry) => entry.id === question.id);
                const isActive = index === safeIndex;
                const verdict = questionVerdicts[question.id];
                const dot = questionDotStyle(verdict, Boolean(question.aiFlagged));
                return (
                  <button
                    key={question.id}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    style={{
                      appearance: "none",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderTop: "1px solid var(--ua-border)",
                      borderLeft: isActive ? "3px solid var(--ua-red)" : "3px solid transparent",
                      background: isActive ? "#f8fafc" : "var(--ua-white)",
                      padding: "10px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "9px",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        flexShrink: 0,
                        marginTop: "2px",
                        background: dot.background,
                        color: dot.color,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "9px",
                        fontWeight: 900,
                      }}
                    >
                      {dot.glyph}
                    </span>
                    <span style={{ display: "grid", gap: "2px", minWidth: 0 }}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "var(--ua-muted)",
                        }}
                      >
                        {question.luik}
                        {question.aiFlagged && (
                          <span
                            aria-hidden
                            title="AI-signaal"
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "var(--ua-red)",
                              display: "inline-block",
                            }}
                          />
                        )}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--ua-navy)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "210px",
                        }}
                      >
                        {question.question}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      <div style={{ display: "grid", gap: "14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: "var(--ua-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {active.luik}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={safeIndex === 0}
              onClick={() => setActiveIndex(Math.max(0, safeIndex - 1))}
              style={{ padding: "5px 10px", fontSize: "12px" }}
            >
              ←
            </button>
            <span style={{ fontSize: "12px", color: "var(--ua-muted)" }}>
              Vraag {safeIndex + 1} van {total}
            </span>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={safeIndex === total - 1}
              onClick={() => setActiveIndex(Math.min(total - 1, safeIndex + 1))}
              style={{ padding: "5px 10px", fontSize: "12px" }}
            >
              →
            </button>
          </div>
        </div>

        <h3 style={{ fontSize: "17px", margin: 0, color: "var(--ua-navy)" }}>{active.question}</h3>

        <div style={{ display: "grid", gap: "6px" }}>
          <span className="section-kicker">Antwoord van de onderzoeker</span>
          <div
            style={{
              border: "1px solid var(--ua-border)",
              borderRadius: "8px",
              padding: "12px 14px",
              background: "var(--ua-bg)",
              fontSize: "14px",
              lineHeight: 1.65,
              color: "var(--ua-ink)",
            }}
          >
            {active.answer}
          </div>
        </div>

        {active.aiFlagged && active.aiNote && (
          <div
            style={{
              border: "1px solid #c5d0e8",
              borderLeft: "3px solid var(--ua-blue)",
              borderRadius: "6px",
              background: "#eef4fb",
              padding: "10px 12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px" }}>
              <span
                aria-hidden
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "var(--ua-blue)",
                  color: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                i
              </span>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "var(--ua-blue)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                AI-signaal
              </span>
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: "var(--ua-muted)", lineHeight: 1.6 }}>
              {active.aiNote}
            </p>
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            className={activeVerdict === "geen-opmerking" ? "btn btn--primary" : "btn btn--ghost"}
            onClick={() => handleVerdict(active, "geen-opmerking")}
            style={{ padding: "7px 12px", fontSize: "12px" }}
          >
            ✓ Geen opmerking
          </button>
          <button
            type="button"
            className={activeVerdict === "aandachtspunt" ? "btn btn--primary" : "btn btn--ghost"}
            onClick={() => handleVerdict(active, "aandachtspunt")}
            style={{ padding: "7px 12px", fontSize: "12px" }}
          >
            ! Aandachtspunt
          </button>
        </div>

        {activeVerdict === "aandachtspunt" && (
          <div style={{ display: "grid", gap: "6px" }}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                fontWeight: 700,
                color: "var(--ua-navy)",
              }}
            >
              {isUnEditedAiSuggestion ? (
                <>
                  AI-voorstel voor feedback
                  <AiNoteBadge verified={false} />
                </>
              ) : (
                "Feedback"
              )}
            </span>
            <textarea
              rows={3}
              value={noteValue}
              onChange={(event) => onQuestionNote(active.id, event.target.value)}
              style={{
                fontFamily: "inherit",
                fontSize: "13px",
                lineHeight: 1.6,
                color: "var(--ua-navy)",
                background: "var(--ua-white)",
                border: "1px solid var(--ua-border)",
                borderRadius: "8px",
                padding: "10px 12px",
                width: "100%",
                resize: "vertical",
              }}
            />
            <span style={{ fontSize: "12px", color: "var(--ua-muted)", lineHeight: 1.5 }}>
              Dit aandachtspunt verschijnt in stap Bevindingen, waar je de tekst verifieert voordat
              die naar de onderzoeker gaat.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function IssuesStep({
  issues,
  decisions,
  noteDrafts,
  onDecision,
  onNoteChange,
}: {
  issues: ReviewIssue[];
  decisions: Record<string, ReviewDecision>;
  noteDrafts: Record<string, string>;
  onDecision: (issueId: string, decision: ReviewDecision | undefined) => void;
  onNoteChange: (issueId: string, text: string) => void;
}) {
  if (issues.length === 0) {
    return (
      <div
        style={{
          border: "1px dashed var(--ua-border)",
          borderRadius: "8px",
          padding: "20px",
          background: "#f8fafc",
          color: "var(--ua-muted)",
          fontSize: "14px",
          lineHeight: 1.6,
        }}
      >
        Geen aandachtspunten. Markeer een vraag als “Aandachtspunt” in stap Nalopen om ze hier te
        laten verschijnen.
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: "14px" }}>
      {issues.map((issue) => {
        const decision = decisions[issue.id];
        const verified = decision === "Geverifieerd";
        const dismissed = decision === "Niet relevant";
        const noteValue = noteDrafts[issue.id] ?? issue.reviewerNote;

        return (
          <div
            key={issue.id}
            style={{ border: "1px solid var(--ua-border)", borderRadius: "8px", padding: "16px" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
              <div>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <h3 style={{ fontSize: "16px", margin: 0 }}>{issue.title}</h3>
                  <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--ua-muted)" }}>
                    · {issue.luik}
                  </span>
                </span>
                <p className="ua-muted-xs" style={{ marginTop: "4px" }}>
                  {issue.source}
                  {issue.tip ? ` · ${issue.tip}` : ""}
                </p>
              </div>
              <SeverityBadge severity={issue.severity} />
            </div>

            <div
              style={{
                marginTop: "12px",
                border: "1px solid #c5d0e8",
                borderLeft: "3px solid var(--ua-blue)",
                borderRadius: "6px",
                background: "#eef4fb",
                padding: "10px 12px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px" }}
              >
                <span
                  aria-hidden
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "var(--ua-blue)",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  i
                </span>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "var(--ua-blue)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  AI-signaal
                </span>
              </div>
              <p style={{ margin: 0, fontSize: "13px", color: "var(--ua-muted)", lineHeight: 1.6 }}>
                {issue.aiFinding}
              </p>
            </div>

            <div style={{ marginTop: "14px", display: "grid", gap: "6px" }}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "var(--ua-navy)",
                }}
              >
                AI-voorstel voor feedback
                <AiNoteBadge verified={verified} />
              </span>
              <textarea
                rows={3}
                value={dismissed ? "" : noteValue}
                disabled={dismissed}
                onChange={(e) => onNoteChange(issue.id, e.target.value)}
                placeholder={dismissed ? "Gemarkeerd als niet relevant" : undefined}
                style={{
                  fontFamily: "inherit",
                  fontSize: "13px",
                  lineHeight: 1.6,
                  color: "var(--ua-navy)",
                  background: dismissed ? "var(--ua-bg)" : "var(--ua-white)",
                  border: "1px solid var(--ua-border)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  width: "100%",
                  resize: "vertical",
                }}
              />
              <span style={{ fontSize: "12px", color: "var(--ua-muted)", lineHeight: 1.5 }}>
                Pas het voorstel aan waar nodig: dit wordt de tekst die naar de onderzoeker gaat.
                Bewerken na verifiëren vraagt een nieuwe verificatie.
              </span>
            </div>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
              {verified ? (
                <span style={{ fontSize: "12px", color: "var(--ua-success)", fontWeight: 700 }}>
                  ✓ Geverifieerd: deze tekst wordt gebruikt
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={dismissed}
                  onClick={() => onDecision(issue.id, "Geverifieerd")}
                  style={{ padding: "7px 12px", fontSize: "12px" }}
                >
                  ✓ Verifieer AI-tekst
                </button>
              )}
              <button
                type="button"
                className={dismissed ? "btn btn--primary" : "btn btn--ghost"}
                onClick={() => onDecision(issue.id, dismissed ? undefined : "Niet relevant")}
                style={{ padding: "7px 12px", fontSize: "12px" }}
              >
                {dismissed ? "Niet relevant ✓ (klik om terug te zetten)" : "Niet relevant"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdviceStep({
  review,
  issueCount,
  decisionCount,
}: {
  review: AssignedReview;
  issueCount: number;
  decisionCount: number;
}) {
  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <div className="stats" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <div className="stat">
          <div className="stat__label">Bevindingen behandeld</div>
          <div className="stat__value">
            {decisionCount}/{issueCount}
          </div>
          <div className="stat__delta">menselijk gevalideerd</div>
        </div>
        <div className="stat">
          <div className="stat__label">Voorgesteld oordeel</div>
          <div className="stat__value" style={{ fontSize: "22px" }}>
            Voorw.
          </div>
          <div className="stat__delta">aanpasbaar</div>
        </div>
        <div className="stat">
          <div className="stat__label">Bronnen</div>
          <div className="stat__value">{review.dossierPieces.length}</div>
          <div className="stat__delta up">traceerbaar</div>
        </div>
      </div>
      <label style={{ display: "grid", gap: "8px" }}>
        <span className="section-kicker">Conceptfeedback voor bureau/secretariaat</span>
        <textarea
          defaultValue={review.draftAdvice}
          rows={6}
          style={{
            width: "100%",
            border: "1px solid var(--ua-border)",
            borderRadius: "8px",
            padding: "12px",
            font: "inherit",
            fontSize: "14px",
            lineHeight: 1.6,
            resize: "vertical",
          }}
        />
      </label>
    </div>
  );
}

function BureauAgenda() {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h2>Bureau-agenda woensdag 2 juli 2026</h2>
          <p className="ua-muted-sm">
            Contextoverzicht voor de komende bespreking. Dit is secundair aan je eigen reviewflow.
          </p>
        </div>
        <span className="badge badge--info">12 dossiers</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Ref.</th>
            <th>Onderzoeker / Project</th>
            <th>Reviewer</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {AGENDA_ROWS.map(([ref, researcher, project, reviewer, status]) => (
            <tr key={ref}>
              <td className="ua-muted-xs">{ref}</td>
              <td>
                <div className="ua-bold-sm">{researcher}</div>
                <div className="ua-muted-xs">{project}</div>
              </td>
              <td className="text-[13px]">{reviewer}</td>
              <td>
                <span className={`badge badge--${agendaStatusVariant(status)}`}>{status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="ua-pager-info">
        + 6 overige dossiers op de agenda · reviewerstatus is bewust eenvoudig gehouden
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ReviewStatus }) {
  const label =
    status === "done" ? "Gereviewd" : status === "progress" ? "Bezig" : "Nog te reviewen";
  const variant = status === "done" ? "success" : status === "progress" ? "warning" : "danger";
  return <span className={`badge badge--${variant}`}>{label}</span>;
}

function SeverityBadge({ severity }: { severity: ReviewIssue["severity"] }) {
  const variant = severity === "hoog" ? "danger" : severity === "midden" ? "warning" : "info";
  return <span className={`badge badge--${variant}`}>{severity}</span>;
}

function AiNoteBadge({ verified }: { verified: boolean }) {
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: 700,
        background: verified ? "#edfaf2" : "#eef2f9",
        border: `1px solid ${verified ? "#a3e4c1" : "#c5d0e8"}`,
        color: verified ? "var(--ua-success)" : "var(--ua-navy)",
        borderRadius: "999px",
        padding: "1px 8px",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {verified ? "✓ Geverifieerd" : "✦ AI-voorstel"}
    </span>
  );
}

function agendaStatusVariant(status: string) {
  if (status === "Gereviewd") {
    return "success";
  }
  if (status === "Bezig") {
    return "warning";
  }
  return "info";
}
